import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';

import ConversationMetaThrottleManager from 'dashboard/helper/ConversationMetaThrottleManager';

// /meta 的最小刷新间隔固定为 10 秒，不再根据 allCount 在 2 秒/10 秒间切换。
// 固定窗口消除 allCount 临界值附近从 10 秒退回 2 秒的请求放大。
export const META_REFRESH_INTERVAL = 10000;

// 仅保留页面实际展示的三个 Tab 数字；assigned_count 由接口返回但当前 UI 不单独存储。
const state = {
  mineCount: 0,
  unAssignedCount: 0,
  allCount: 0,
};

export const getters = {
  getStats: $state => $state,
};

export const shouldThrottle = () => {
  // 所有调用方统一使用固定阈值，避免重新引入基于会话数量的分档。
  return ConversationMetaThrottleManager.shouldThrottle(META_REFRESH_INTERVAL);
};

// SSR/单测环境可能没有 document；此时按可见页面处理，保证纯函数测试可运行。
const isPageHidden = () => typeof document !== 'undefined' && document.hidden;

export const actions = {
  get: async ({ commit }, params) => {
    // 隐藏 Tab 的 ActionCable 仍会接收事件，但这里不发网络请求；
    // 页面恢复可见时由 resume 使用当前筛选条件补偿刷新一次。
    if (isPageHidden()) {
      return;
    }

    // 固定 10 秒窗口内的重复事件直接丢弃，不创建尾随定时器。
    // 这是本次最小治理方案的取舍：优先约束 /meta 频率，统计数字可能暂时滞后。
    if (shouldThrottle()) {
      // eslint-disable-next-line no-console
      console.warn('Throttle /meta fetch for fixed 10-second window');
      return;
    }

    // 在请求开始前记录时间，使“最小间隔”约束的是请求开始时间，而不是响应完成时间。
    ConversationMetaThrottleManager.markUpdate();

    try {
      const response = await ConversationApi.meta(params);
      // 保持原接口契约，只把 data.meta 提交给统计 mutation。
      const {
        data: { meta },
      } = response;
      commit(types.SET_CONV_TAB_META, meta);
    } catch (error) {
      // 统计数字属于辅助信息，失败不能阻断会话列表、回复或分配操作。
      // 不在此处无上限重试；后续 ActionCable 事件或恢复可见会再次刷新。
    }
  },
  resume({ dispatch }, params) {
    // visibilitychange 可能在页面仍 hidden 时重复触发，防止误发请求。
    if (isPageHidden()) {
      return;
    }

    // 恢复可见时重置节流时间，并使用页面当前筛选条件立即补偿刷新一次。
    ConversationMetaThrottleManager.reset();
    return dispatch('get', params);
  },
  set({ commit }, meta) {
    commit(types.SET_CONV_TAB_META, meta);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
    } = {}
  ) {
    // 服务端字段使用 snake_case，Vuex state 沿用既有 camelCase 命名。
    $state.mineCount = mineCount;
    $state.allCount = allCount;
    $state.unAssignedCount = unAssignedCount;

    // updatedOn 仅记录本地 Store 最近一次成功提交时间，不参与新的固定节流判断。
    $state.updatedOn = new Date();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
